import { useEffect, useState } from 'react'
import {
  Terminal, ArrowRight, Copy, Check, X, Key,
} from 'lucide-react'
import ModalPortal from './ModalPortal'
import ApiTokenControls from './ApiTokenControls'
import { AgentPromptBox, useAgentPromptCopy } from './AgentPrompt'
// The np-* styling for these callouts + modals lives in StartQaForm.css (the home of that design
// system). Import it so the callouts are styled wherever they're used (the classic StartQaForm and
// the chat-based OnboardingChat both render these).
import './StartQaForm.css'

// The two onboarding callouts shared by the classic new-project form and the chat-based onboarding:
// "Want your coding agent to handle it?" (AgentCta) and "Want to QA your app for free?" (FreeQaCta).
// Each is self-contained (banner button + the modal it opens).

/* "Want your coding agent to handle it?" banner + the prompt modal it opens. */
export function AgentCta() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="np-agentcta" onClick={() => setOpen(true)} data-testid="agent-cta" data-analytics-event="dashboard-agent-cta-open">
        <span className="np-agentcta__icon" aria-hidden>
          <Terminal size={17} strokeWidth={1.75} />
        </span>
        <span className="np-agentcta__txt">
          <span className="np-agentcta__title">Want your coding agent to handle it?</span>
        </span>
        <span className="np-agentcta__action">
          Get the prompt <ArrowRight size={15} strokeWidth={1.75} aria-hidden />
        </span>
      </button>
      {open && <AgentPromptModal onClose={() => setOpen(false)} />}
    </>
  )
}

function AgentPromptModal({ onClose }: { onClose: () => void }) {
  const { copied, copy } = useAgentPromptCopy()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <ModalPortal>
    <div
      className="np-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="np-modal" role="dialog" aria-modal="true" aria-label="Start QA from your coding agent" data-testid="agent-panel">
        <div className="np-modal__head">
          <div className="np-modal__heading">
            <span className="np-modal__icon" aria-hidden>
              <Terminal size={18} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="np-modal__title">Start QA from your coding agent</h2>
              <p className="np-modal__lead">
                Paste this prompt into Claude Code, Cursor, Codex, or any coding assistant. It points your
                agent at the Replay QA agent docs, which cover the full QA and fix loop.
              </p>
            </div>
          </div>
          <button type="button" className="np-modal__close" onClick={onClose} aria-label="Close" data-analytics-event="dashboard-agent-modal-close">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="np-modal__body">
          <AgentPromptBox copied={copied} onCopy={copy} analyticsEvent="dashboard-agent-prompt-copy" />

          <div className="np-agent-token" data-testid="agent-token">
            <div className="np-agent-token__head">
              <span className="np-agent-token__title">
                <Key size={14} strokeWidth={1.75} aria-hidden /> Your API tokens
              </span>
              <p className="np-agent-token__lead">
                The CLI can sign in interactively. For non-interactive or direct API access, generate
                a token here and give it to your agent when asked. It is shown only once.
              </p>
            </div>
            <ApiTokenControls />
          </div>
        </div>

        <div className="np-modal__foot">
          <button type="button" className="np-submit" onClick={copy} data-analytics-event="dashboard-agent-prompt-copy">
            {copied ? <Check size={15} strokeWidth={1.75} aria-hidden /> : <Copy size={15} strokeWidth={1.75} aria-hidden />}
            {copied ? 'Prompt copied' : 'Copy prompt'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}
